import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Grid,
  Divider,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Link,
  LinearProgress
} from '@mui/material';
import useApprover from 'app/hooks/useApprover';
import { CheckCircle, Cancel, HourglassBottom, EditRounded, DraftsOutlined  } from '@mui/icons-material';
import _ from "lodash";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ApproveButton from 'app/components/Button/ApproveButton';
import { useUpload } from "app/contexts/UploadContext";
import ViewDocument from 'app/components/Viewer/ViewDocument.jsx';
import AccumulationCard from 'app/components/Card/AccumulationCard.js';
import "../../extensions/string.extensions.js";
import "app/asset/css/watermark.css"

dayjs.extend(utc);
dayjs.extend(timezone);

const DisplayRow = ({ label = 'No label', value = '-' }) => {
  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Grid item xs={3} md={2}>
        <Typography fontWeight="bold">{label}</Typography>
      </Grid>
      <Grid item xs={9} md={10}>
        <Typography fontWeight="bold">: {value}</Typography>
      </Grid>
    </Grid>
  );
};

const getStatusInfo = (id) => {
  switch (id) {
    case 1:
      return { icon: <HourglassBottom sx={{ color: "#fff !important" }} />, text: 'SUBMITTED TASK', color: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' };
    case 3:
      return { icon: <EditRounded sx={{ color: "#fff !important" }} />, text: 'REVISED TASK', color: 'linear-gradient(45deg, #FFEB3B 30%, #FFC107 90%)' };
    case 4:
      return { icon: <CheckCircle sx={{ color: "#fff !important" }} />, text: 'APPROVED TASK', color: 'linear-gradient(45deg, #4CAF50 30%, #388E3C 90%)' };
    case 99:
      return { icon: <Cancel sx={{ color: "#fff !important" }} />, text: 'CANCELLED TASK', color: 'linear-gradient(45deg, #F44336 30%, #D32F2F 90%)' };
    case 999:
      return { icon: <Cancel sx={{ color: "#fff !important" }} />, text: 'REJECTED TASK', color: 'linear-gradient(45deg, #F44336 30%, #D32F2F 90%)' };
    case 111:
      return { icon: <DraftsOutlined sx={{ color: "#fff !important" }} />, text: 'DRAFTED TASK', color: 'linear-gradient(45deg, #656565 30%, #514e4e 90%)' };
    default:
      return { icon: null, text: 'WAITING TASK', color: 'gray' };
  }
};

const getDisplayName = (name) =>{
    return name?.replace(/\s*\[[^\]]*]/g, "").trim() || "-";
}

const getApprovers = (track) => {
  if (!track) return [];
  const approvers = track.approvers || [];
  const verificators = _.flatMap(track.templates, "verificator");
  const users = [...approvers, ...verificators];

  const names = _.uniq(_.map(users, (u) => getDisplayName(u.UserName)));

  return names.join(", ");
};

const getAssigners = (track) => {
  if (!track?.templates) return [];
  const assigners = _.map(track.templates, "assigner");
  const names = _.uniq(_.map(assigners, (a) => getDisplayName(a?.UserName)));

  return names.join(", ");
};

const ItemVerifier = ({project}) => {
  const { SubmitApprovers } = useApprover();
  const { getDocument } = useUpload();

  const JSON_TYPE = ['autocomplete','multiautocomplete','checkbox','dropdownlist','dropdownlistmultiple','radio','rangedate'];
  const HTML_TYPE = ['texteditor'];
  const DOC_TYPE = ["document"];

  const [openDoc, setOpenDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState({ filename: null, projectCode: project?.TransCode });

  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleOpenDocument = (filename) => {
    setSelectedDoc({ filename, projectCode: project?.TransCode });
    setOpenDoc(true);
  };

  const handleCloseDocument = () => {
    setOpenDoc(false);
    setSelectedDoc({ filename: null, projectCode: project?.TransCode });
  };

  const handleDownload = async (filename) => {
    try {
        setDownloading(true);
        setProgress(0);

        const doc = await getDocument(filename, project?.TransCode, (loaded, total) => {
        if (total > 0) {
            setProgress(Math.round((loaded / total) * 100));
        }
        });

        if (!(doc.data instanceof Blob)) {
            throw new Error("Invalid document format !");
        }

        const url = URL.createObjectURL(doc.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.filename || filename;
        a.click();
        URL.revokeObjectURL(url);

        setDownloading(false);
        setProgress(100);
    } catch (err) {
        console.error("Download Failed:", err);
        setDownloading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }} className={`watermark-approver watermark-${project?.Status}`}>
      <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, mb: 2 }}>
        <Grid container justifyContent="center" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            {project?.Title?.toUpperCase() || '-'}
          </Typography>
        </Grid>

        <DisplayRow label="DOCUMENT NO" value={project?.TransCode} />
        <DisplayRow label="FROM" value={getAssigners(project || {} )}/>
        <DisplayRow label="TO" value={getApprovers(project || {} )}/>
        <DisplayRow label="CREATED DATE" value={dayjs(project?.CrtAt).utcOffset(7).format('DD MMMM YYYY, HH:mm')}/>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={3} md={2}>
                <Typography fontWeight="bold">DESCRIPTION</Typography>
            </Grid>
            <Grid item xs={9} md={10}>
                <Typography><b>:</b> {project?.Description}</Typography>
            </Grid>
        </Grid>
        {/* <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={3} md={2}>
                <Typography fontWeight="bold">ATTACHMENTS</Typography>
            </Grid>
            <Grid item xs={9} md={10}>
                {project?.documents?.length > 0 ? (
                <Stack direction="row" spacing={0.5}>
                    <b style={{ color: "black", fontWeight: "bold"}}>:</b>
                    {project.documents.map((doc, i) => (
                        <Link
                            key={i}
                            href={doc?.FileUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="body2"
                        >
                            {doc?.FileName || `Document ${i + 1}`}
                        </Link>
                    ))}
                </Stack>
                ) : (
                    <Typography variant="body2" color="error"><b style={{ color: "black", fontWeight: "bold"}}>:</b> -- No Attachments --</Typography>
                )}
            </Grid>
        </Grid> */}
      </Box>

      {project?.templates?.map((tpl, i) => (
        <Box key={i} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 2}}>
          {tpl?.contents?.map((t, idx) => (
            <div key={idx}>
              <Grid container justifyContent="space-between">
                <Grid item>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt : (idx > 0 ? 2 : 0.65)}}> {t.TcName}</Typography>
                </Grid>
                { idx === 0 && 
                    <Grid item alignItems="end">
                        <Chip
                            icon={getStatusInfo(tpl?.Status)?.icon}
                            label={getStatusInfo(tpl?.Status)?.text}
                            sx={{
                                color: '#fff',
                                background: getStatusInfo(tpl?.Status)?.color,
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                borderRadius: 0.25
                            }}
                        />
                    </Grid>
                }
              </Grid>
              <Divider sx={{ mb: 2 }} />

              {t.TcType === 'Form' && Array.isArray(t.fields) && (
                <Grid container spacing={2}>
                  {t.fields.map((field, fieldIdx) => {
                    const tdValue = field?.items?.[0]?.TdValue;
                    let displayValue = '-';

                    if (DOC_TYPE.includes(field.TfType)) {
                        let document = [];
                        try {
                            const parsed = JSON.parse(tdValue || '[]');
                            document = Array.isArray(parsed)
                            ? parsed
                            : parsed
                                ? [parsed]
                                : [];
                        } catch (err) {
                            document = tdValue ? [tdValue] : [];
                        }

                        return (
                            <Grid item xs={12} key={fieldIdx}>
                            <Typography fontWeight="bold">{field.TfLabel}:</Typography>

                            {document.length > 0 ? (
                                document.map((val, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: idx > 0 ? 1 : 0 }}>
                                    <Link component="button" variant="body2" onClick={() => handleOpenDocument(val)}>
                                    {val}
                                    </Link>
                                    {" | "}
                                    <Link component="button" variant="body2" onClick={() => handleDownload(val)}>
                                    Download
                                    </Link>

                                    {downloading && idx === 0 && (
                                    <Box sx={{ mt: 1, width: '100%' }}>
                                        <LinearProgress variant="determinate" value={progress} />
                                        <Typography variant="caption">{progress}%</Typography>
                                    </Box>
                                    )}
                                </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">No file</Typography>
                            )}
                            </Grid>
                        );
                    }

                    if (JSON_TYPE.includes(field.TfType)) {
                      try {
                          const parsed = JSON.parse(tdValue || '[]');
                          displayValue = Array.isArray(parsed)
                          ? parsed.map((item) => field.TfType == 'rangedate' ?  item.value.toFormattedDate() : item.value).join(', ')
                          : '-';
                      } catch {
                          displayValue = '-';
                      }
                    } else if (HTML_TYPE.includes(field.TfType)) {
                      return (
                        <Grid item xs={12} key={fieldIdx}>
                          <Typography fontWeight="bold">{field.TfLabel}:</Typography>
                          <Box
                            sx={{
                              border: '1px solid #ccc',
                              p: 1,
                              mt: 1,
                              borderRadius: 1,
                              backgroundColor: '#f9f9f9',
                              overflowX: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: tdValue || '-' }}
                          />
                        </Grid>
                      );
                    } else {
                      displayValue =  field.TfType == 'currency' ? tdValue.toFormatCurrency() :  tdValue ?? '-';
                    }

                    return (
                      <Grid item xs={12} sm={6} key={fieldIdx}>
                        <Typography variant="body2" fontWeight="bold">
                          {field.TfLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {displayValue}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {t.TcType === 'Datarow' && Array.isArray(t.fields) && (
                <TableContainer component={Paper} style={{ overflowX: "auto" }}>
                  <Table size="small" style={{ tableLayout: "auto", minWidth: (t.fields.length * 120), border: '1px solid #ccc', borderCollapse: 'collapse'}}>
                    <TableHead className="bg-dark text-white">
                        <TableRow>
                        <TableCell align="center" className="text-white" sx={{ fontWeight: 'bold', border: '1px solid #ccc', width: 50 }}>
                            No
                        </TableCell>
                        {t.fields.map((field, colIdx) => (
                            <TableCell
                            key={colIdx}
                            align="center"
                            sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}
                            className="text-white"
                            >
                            {field.TfLabel}
                            </TableCell>
                        ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(t.fields[0]?.items || []).map((_, rowIdx) => (
                        <TableRow key={rowIdx}>
                            <TableCell align="center" sx={{ border: '1px solid #ccc'}}>
                            {rowIdx + 1}
                            </TableCell>
                            {t.fields.map((field, colIdx) => {
                            const rowValue = field.items?.[rowIdx]?.TdValue ?? '-';
                            const isCurrency = field.TfType === 'currency';
                            const isNumber = field.TfFormat === 'integer';

                            let formattedValue = rowValue;

                            if (JSON_TYPE.includes(field.TfType)) {
                                try {
                                const parsed = JSON.parse(rowValue || '[]');
                                formattedValue = Array.isArray(parsed)
                                    ? parsed.map((item) => item.value).join(', ')
                                    : parsed?.value || '-';
                                } catch {
                                formattedValue = '-';
                                }
                            } else if (isCurrency) {
                                formattedValue = parseFloat(rowValue).toLocaleString('id-ID');
                            }

                            return (
                                <TableCell
                                key={colIdx}
                                align={isCurrency || isNumber ? 'right' : 'left'}
                                sx={{ border: '1px solid #ccc', pl : (isCurrency || isNumber ? 0 : "5px") }}
                                >
                                {formattedValue || '-'}
                                </TableCell>
                            );
                            })}
                        </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          ))}
          {
          Boolean(tpl.IsUseBudget) && 
            <AccumulationCard projectCode={project?.TransCode || undefined } />
          }
        </Box>
      ))}
      { 
        Boolean(project?.CanApprove) && (
            <Grid container justifyContent="end" alignItems="center">
                <Box>
                    <br/>
                    <ApproveButton
                        project_code={project.TransCode}
                        onApprove={async ({ project_code, notes, status }) => {
                            const formData = new FormData();
                            formData.append('project_code', project_code);
                            formData.append('status', status);
                            formData.append('notes', notes);

                            await SubmitApprovers(formData, () => {}, true);
                        }}
                        onReject={async ({ project_code, notes, status }) => {
                            const formData = new FormData();
                            formData.append('project_code', project_code);
                            formData.append('status', status);
                            formData.append('notes', notes);

                            await SubmitApprovers(formData, () => {}, true);
                        }}
                    />
                </Box>
            </Grid>
        )
      }

      {openDoc && (
          <ViewDocument
              open={openDoc}
              filename={selectedDoc.filename}
              projectCode={selectedDoc.projectCode ?? project?.TransCode }
              onClose={handleCloseDocument}
          />
      )}
    </Paper>
  );
};

export default ItemVerifier;